import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { AxiosResponse } from 'axios';
import { TLabelAuto } from '../types/label-auto.type';

/**
 *
 */
export const createLabelAutoValidationSchema = z
  .object({
    name: z.string().min(1),
    color: z.string().min(1),
    teamId: z.string().min(1),
    field: z.enum(['isCustomerAuthorized', 'totalFollower', 'content']),
    organizationChannelIds: z.array(z.string().min(1)).min(1),
    valueContent: z
      .object({
        type: z.enum(['or', 'and']),
        words: z.array(z.string().min(1)).min(1),
      })
      .optional(),
    valueTotalFollower: z.number().min(1).optional(),
    valueIsCustomerAuthorized: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.field === 'content') {
        return data.valueContent !== null && data.valueContent !== undefined;
      }
      return true;
    },
    {
      message: 'Content value is required when field is content',
      path: ['valueContent'],
    }
  )
  .refine(
    (data) => {
      if (data.field === 'totalFollower') {
        return data.valueTotalFollower !== null && data.valueTotalFollower !== undefined;
      }
      return true;
    },
    {
      message: 'Total follower value is required when field is total follower',
      path: ['valueTotalFollower'],
    }
  )
  .refine(
    (data) => {
      if (data.field === 'isCustomerAuthorized') {
        return data.valueIsCustomerAuthorized !== null && data.valueIsCustomerAuthorized !== undefined;
      }
      return true;
    },
    {
      message: 'User type value is required when field is user type',
      path: ['valueIsCustomerAuthorized'],
    }
  );
export type TCreateLabelAutoValidation = z.infer<typeof createLabelAutoValidationSchema>;
export type TCreateLabelAutoPayload = TCreateLabelAutoValidation;
export type TCreateLabelAutoResponse = AxiosResponse<ApiResponse<TLabelAuto>>;

export const createLabelAuto = async (payload: TCreateLabelAutoPayload): Promise<TCreateLabelAutoResponse> => {
  const url = '/auto-label';

  const toBeSendData: {
    name: string;
    color: string;
    teamId: string;
    field: 'isCustomerAuthorized' | 'totalFollower' | 'content';
    organizationChannelIds: string[];
    value: { type: 'or' | 'and'; words: string[] } | number | boolean | undefined;
  } = {
    name: payload.name,
    color: payload.color,
    teamId: payload.teamId,
    field: payload.field,
    organizationChannelIds: payload.organizationChannelIds,
    value: undefined,
  };

  if (payload.field === 'content' && payload.valueContent) {
    toBeSendData.value = {
      type: payload.valueContent.type,
      words: payload.valueContent.words,
    };
  } else if (payload.field === 'totalFollower') {
    toBeSendData.value = payload.valueTotalFollower ?? undefined;
  } else if (payload.field === 'isCustomerAuthorized') {
    toBeSendData.value = payload.valueIsCustomerAuthorized ?? undefined;
  }

  return axiosInstance.post(url, toBeSendData);
};

/**
 * Use Create Label Auto React-Query Hook
 */
type TUseCreateLabelAutoMutationOptions = {
  mutationConfigOptions?: TypedMutationConfig<typeof createLabelAuto, AxiosErrorCustom>;
};

export const useCreateLabelAutoMutation = ({ mutationConfigOptions }: TUseCreateLabelAutoMutationOptions) => {
  const { ...restConfig } = mutationConfigOptions || {};

  return useMutation({
    ...restConfig,
    mutationFn: createLabelAuto,
  });
};
